import { useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DialogContent, DialogActions } from '@mui/material';
import { useGroups } from '@app/api/groups/useGroups';
import { roles } from '@shared/static/roles';
import { ThemeButton } from '@shared/ui/buttons';
import {
  TeamNameInput,
  TeamleadPicker,
  AvatarPicker,
  AvatarColorPicker,
  CardsActionPicker,
  BasicSelectInput,
  MemberRoleView,
} from '@shared/ui/forms/inputs';
import Note from '@shared/ui/messages/Note';
import { getDirtyValues, getTotalCards } from '@utils/helpers';
import { validateServerErrors } from '@utils/validation';

export default function CreateTeamDialog({
  close,
  data,
  setTitle,
  setImage,
  setSubtitle,
  setHideCloseButton,
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(data?.name);
  const activeTeamLead = isEdit ? (data.teamLead ?? null) : null;

  const [isCardsScreen, setIsCardsScreen] = useState(false);
  const [availableTeamLeads, setAvailableTeamLeads] = useState([]);

  const { createGroup, updateGroup, fetchAvailableTeamLeads } = useGroups();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, dirtyFields, isDirty, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: data?.name || '',
      avatar: {
        name: data?.avatar?.name || 'google',
        color: data?.avatar?.color || '#4D72ED',
      },
      teamLead_id: data?.teamLead?.id || null,
      teamlead_transfer: {
        new_teamlead_user_id: data?.teamLead?.id || null,
        cards_action: null,
        cards_transfer_user_id: null,
      },
    },
  });

  const teamleadTransferWatch = useWatch({
    control,
    name: 'teamlead_transfer',
  });

  const activeTeamLeadCardsCount = getTotalCards(activeTeamLead);

  const needCardsAction =
    teamleadTransferWatch?.new_teamlead_user_id !== activeTeamLead?.id &&
    activeTeamLeadCardsCount > 0;

  const buttonText = !isEdit
    ? t('create')
    : needCardsAction
      ? t('continue')
      : t('save');

  const users = data?.users ?? [];

  const availableUsers = users.filter((x) => x.id !== activeTeamLead?.id);

  function buildTeamleadTransfer(payload, formData) {
    if (!payload.teamlead_transfer) return payload;

    const { cards_action, new_teamlead_user_id } = payload.teamlead_transfer;
    const hasCards = activeTeamLeadCardsCount > 0;

    const teamlead_transfer = {
      new_teamlead_user_id,
      ...(hasCards && { cards_action }),
      ...(cards_action === 'transfer' && {
        cards_transfer_user_id:
          formData.teamlead_transfer.cards_transfer_user_id,
      }),
    };

    return { ...payload, teamlead_transfer };
  }

  async function handleCreate(formData) {
    try {
      const { name, avatar, teamLead_id } = formData;
      const res = await createGroup({ name, avatar, teamLead_id });
      if (res) close();
    } catch (err) {
      validateServerErrors(setError, err);
    }
  }

  async function handleUpdate(formData) {
    if (!data?.id) return;

    try {
      let payload = getDirtyValues(dirtyFields, formData);
      payload = buildTeamleadTransfer(payload, formData);

      const res = await updateGroup(
        {
          group: {
            ...payload,
            id: data.id,
          },
        },
        data,
      );

      if (res) close();
    } catch (err) {
      validateServerErrors(setError, err);
    }
  }

  async function onSubmit(formData) {
    if (isEdit) {
      await handleUpdate(formData);
    } else {
      await handleCreate(formData);
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const leads = await fetchAvailableTeamLeads();
      if (leads) setAvailableTeamLeads(leads);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isCardsScreen) {
      setTitle('team_lead_cards');
      setImage('cards');
      setHideCloseButton(true);
      setSubtitle('');
    } else if (isEdit) {
      setTitle(data.name);
      setSubtitle(t('team'));
      setImage('');
      setHideCloseButton(false);
    } else {
      setTitle('new_team');
      setImage('');
      setHideCloseButton(false);
      setSubtitle('');
    }
  }, [
    t,
    isCardsScreen,
    isEdit,
    data?.name,
    setTitle,
    setImage,
    setHideCloseButton,
    setSubtitle,
  ]);

  return (
    <>
      <DialogContent>
        {isCardsScreen ? (
          <>
            <div className="form">
              <p>{t('what_to_do_team_lead_cards')}</p>

              {activeTeamLead && (
                <MemberRoleView
                  member={{ ...activeTeamLead, group_role: roles.TEAM_LEAD }}
                />
              )}

              <CardsActionPicker
                error={errors.teamlead_transfer?.cards_action}
                control={control}
                type="editTeam"
                name="teamlead_transfer.cards_action"
              />

              {teamleadTransferWatch?.cards_action === 'transfer' && (
                <BasicSelectInput
                  control={control}
                  name="teamlead_transfer.cards_transfer_user_id"
                  label={t('new_owner')}
                  icon="user-input"
                  placeholder={t('Administrator')}
                  searchable
                  data={availableUsers}
                  error={errors.teamlead_transfer?.cards_transfer_user_id}
                />
              )}
            </div>

            <Note
              type="warning"
              marginTop
              title={t('important')}
              content={t('edit_team_cards_note')}
            >
              <ul>
                <li>{t('edit_team_cards_note_1')}</li>
                <li>{t('edit_team_cards_note_2')}</li>
              </ul>
            </Note>
          </>
        ) : (
          <div className="form create-team-form">
            <TeamNameInput control={control} errors={errors} />
            <TeamleadPicker
              name={
                isEdit
                  ? 'teamlead_transfer.new_teamlead_user_id'
                  : 'teamLead_id'
              }
              currentTeamLead={data?.teamLead}
              control={control}
              errors={errors}
              teamLeads={availableTeamLeads}
            />
            <AvatarPicker control={control} />
            <AvatarColorPicker control={control} />
          </div>
        )}
      </DialogContent>

      <DialogActions>
        {isCardsScreen ? (
          <div className="btns-toolbar">
            <ThemeButton
              onClick={handleSubmit(onSubmit)}
              startIcon="check"
              loading={isSubmitting}
            >
              {t('confirm')}
            </ThemeButton>
            <ThemeButton
              onClick={() => setIsCardsScreen(false)}
              appearance="secondary"
            >
              {t('cancel')}
            </ThemeButton>
          </div>
        ) : (
          <ThemeButton
            onClick={
              needCardsAction
                ? () => setIsCardsScreen(true)
                : handleSubmit(onSubmit)
            }
            startIcon="check"
            loading={isSubmitting}
            disabled={isEdit && !isDirty}
            block={false}
            minWidth
          >
            {buttonText}
          </ThemeButton>
        )}
      </DialogActions>
    </>
  );
}
